import {useEffect, useState} from 'react'
import {useFormik} from 'formik'
import {useParams} from 'react-router-dom'
import {lookupGymMember, MemberLookupResult} from '../core/customerLookup'
import {GymReservation, initialReservation} from '../core/data'
import {getGymOutletOptionByGuid, GymOutletOption} from '../core/outletOptions'
import {reservationSchema} from '../core/validation'
import {GymOutletSelect} from './GymOutletSelect'
import {ReservationSuccessModal} from './ReservationSuccessModal'

export function GymReservationForm() {
  const {outletGuid} = useParams<{outletGuid?: string}>()
  const [lookupResult, setLookupResult] = useState<MemberLookupResult | null>(null)
  const [submitError, setSubmitError] = useState('')
  const [lockedOutlet, setLockedOutlet] = useState<GymOutletOption | null>(null)
  const [outletUrlState, setOutletUrlState] = useState<'idle' | 'loading' | 'locked' | 'invalid'>(
    outletGuid ? 'loading' : 'idle'
  )
  const form = useFormik({
    initialValues: {...initialReservation},
    validationSchema: reservationSchema,
    onSubmit: async (values, helpers) => {
      setSubmitError('')

      try {
        const phone = values.phone.trim()
        const email = values.email.trim().toLowerCase()
        const member = await lookupGymMember(phone, email)

        setLookupResult(
          member
            ? {kind: 'found', member, outletName: values.outletName}
            : {kind: 'not-found', phone, email}
        )
      } catch {
        setSubmitError('Pengecekan member gagal. Periksa koneksi Anda dan coba kembali.')
      } finally {
        helpers.setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    let active = true

    if (!outletGuid) {
      setLockedOutlet(null)
      setOutletUrlState('idle')
      return () => {
        active = false
      }
    }

    setOutletUrlState('loading')
    getGymOutletOptionByGuid(outletGuid).then((outlet) => {
      if (!active) return

      if (outlet) {
        setLockedOutlet(outlet)
        form.setFieldValue('outlet', outlet.value, false)
        form.setFieldValue('outletName', outlet.label, false)
        form.setFieldTouched('outlet', false, false)
        setOutletUrlState('locked')
        return
      }

      setLockedOutlet(null)
      form.setFieldValue('outlet', '', false)
      form.setFieldValue('outletName', '', false)
      setOutletUrlState('invalid')
    })

    return () => {
      active = false
    }
    // Formik setters are stable; the URL GUID is the only trigger for this lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletGuid])
  const error = (field: keyof GymReservation) => form.touched[field] && form.errors[field]
  const accessibility = (field: keyof GymReservation) => ({
    id: `gym-${field}`,
    'aria-invalid': Boolean(error(field)),
    'aria-describedby': error(field) ? `gym-${field}-error` : undefined,
    required: true,
  })
  const feedback = (field: keyof GymReservation) =>
    error(field) ? (
      <div id={`gym-${field}-error`} className='text-danger mt-2' role='alert'>
        {form.errors[field]}
      </div>
    ) : null

  if (outletGuid && outletUrlState === 'loading') {
    return (
      <div className='gym-outlet-block text-center py-10' role='status'>
        <span className='spinner-border text-primary mb-5' aria-hidden='true' />
        <h3 className='fs-3 fw-bold mb-2'>Memeriksa outlet</h3>
        <p className='text-gray-600 mb-0'>Mohon tunggu sebentar...</p>
      </div>
    )
  }

  if (outletGuid && outletUrlState === 'invalid') {
    return (
      <div className='gym-outlet-block text-center py-10' role='alert'>
        <div className='gym-outlet-block-icon d-flex align-items-center justify-content-center mx-auto mb-5'>
          <i className='bi bi-geo-alt-fill fs-1' aria-hidden='true' />
        </div>
        <h3 className='fs-2 fw-bold mb-3'>Outlet tidak ditemukan</h3>
        <p className='text-gray-600 fs-6 mb-2'>
          Tautan outlet tidak valid, sudah tidak aktif, atau bukan bagian dari Square Gym.
        </p>
        <p className='text-gray-600 mb-0'>
          Silakan hubungi staf outlet untuk mendapatkan tautan yang benar.
        </p>
      </div>
    )
  }

  return (
    <>
      <form onSubmit={form.handleSubmit} noValidate>
        <div className='mt-7 mb-5'>
          <label htmlFor='gym-outlet' className='form-label mt-6 required'>
            Outlet Gym
          </label>
          <GymOutletSelect
            value={
              form.values.outlet ? {value: form.values.outlet, label: form.values.outletName} : null
            }
            isClearable={!lockedOutlet}
            isDisabled={outletUrlState === 'loading' || Boolean(lockedOutlet)}
            onBlur={() => form.setFieldTouched('outlet', true)}
            onChange={(newValue) => {
              form.setFieldValue('outlet', newValue?.value || '')
              form.setFieldValue('outletName', newValue?.label || '')
            }}
          />
          {outletUrlState === 'locked' && (
            <div className='text-success fs-8 mt-2'>
              <i className='bi bi-lock-fill me-1' aria-hidden='true' />
              Outlet dipilih otomatis dari tautan.
            </div>
          )}
          {feedback('outlet')}
        </div>
        <div className='row g-5'>
          {(
            [
              ['phone', 'Nomor WhatsApp / HP', 'tel', 'Contoh: 081234567890', 'tel'],
              ['email', 'Email', 'email', 'Contoh: member@email.com', 'email'],
            ] as const
          ).map(([field, label, type, placeholder, autoComplete]) => (
            <div className='col-12' key={field}>
              <label htmlFor={`gym-${field}`} className='form-label required'>
                {label}
              </label>
              <input
                {...form.getFieldProps(field)}
                {...accessibility(field)}
                type={type}
                autoComplete={autoComplete}
                placeholder={placeholder}
                className='form-control form-control-lg'
              />
              {feedback(field)}
            </div>
          ))}
          <div className='col-12'>
            {submitError && (
              <div className='alert alert-danger mb-5' role='alert'>
                {submitError}
              </div>
            )}
            <button
              type='submit'
              className='gym-check-submit btn btn-lg w-100 d-flex align-items-center justify-content-center gap-2'
              disabled={form.isSubmitting}
            >
              {form.isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm' aria-hidden='true' />
                  Memeriksa data...
                </>
              ) : (
                <>
                  <i className='bi bi-search' aria-hidden='true' />
                  Cek Status Member
                </>
              )}
            </button>
          </div>
        </div>
      </form>
      <ReservationSuccessModal result={lookupResult} onClose={() => setLookupResult(null)} />
    </>
  )
}
